import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Calculator, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { CategoryComputation, RatioComputation } from '../types';

// Utility functions
const formatValue = (value: number | null): string => {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
};

const getMethodDescription = (method: string): string => {
    const descriptions: Record<string, string> = {
        'liquidity_higher_better': 'Higher values indicate better liquidity',
        'leverage_lower_better': 'Lower values indicate better leverage',
        'profitability_higher_better': 'Higher values indicate better profitability',
        'coverage_higher_better': 'Higher values indicate better coverage',
        'turnover_higher_better': 'Higher values indicate better efficiency',
        'cash_flow_higher_better': 'Higher values indicate better cash flow',
        'default_higher_better': 'Higher values are generally better',
        'missing_value': 'Data not available for calculation',
        'leverage_zero_debt': 'Perfect score for companies with no debt'
    };
    return descriptions[method] || method;
};

interface ComputationDetailsProps {
    category: string;
    computation: CategoryComputation;
    categoryColor: {
        bg: string;
        text: string;
        icon: string;
    };
    isFocused?: boolean;
    onFocusChange?: (focused: boolean) => void;
    scrollRef?: React.RefObject<HTMLDivElement>;
}

const ComputationDetails: React.FC<ComputationDetailsProps> = ({
    category,
    computation,
    categoryColor,
    isFocused = false,
    onFocusChange,
    scrollRef
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Auto-expand when focused and scroll into view
    React.useEffect(() => {
        if (isFocused) {
            if (!isExpanded) {
                setIsExpanded(true);
            }
            // Scroll to the element after a brief delay to ensure it's rendered
            setTimeout(() => {
                if (scrollRef?.current) {
                    scrollRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            }, 100);
        }
    }, [isFocused, isExpanded, scrollRef]);

    return (
        <div
            ref={scrollRef}
            className={`${categoryColor.bg} border rounded-lg overflow-hidden`}
        >
            <button
                onClick={() => {
                    const newExpanded = !isExpanded;
                    setIsExpanded(newExpanded);
                    if (onFocusChange) {
                        onFocusChange(newExpanded);
                    }
                }}
                className={`w-full p-4 text-left transition-colors hover:opacity-80`}
                type="button"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Calculator className={`w-5 h-5 ${categoryColor.icon}`} />
                        <div>
                            <h4 className={`font-semibold ${categoryColor.text} capitalize`}>
                                {category.replace('_', ' ')} Computation
                            </h4>
                            <p className={`text-sm ${categoryColor.text} opacity-80`}>
                                Final Score: {computation.final_score.toFixed(2)}/100
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${categoryColor.text}`}>
                            {computation.ratios.length} ratio{computation.ratios.length !== 1 ? 's' : ''}
                        </span>
                        {isExpanded ? (
                            <ChevronDown className={`w-5 h-5 ${categoryColor.icon}`} />
                        ) : (
                            <ChevronRight className={`w-5 h-5 ${categoryColor.icon}`} />
                        )}
                    </div>
                </div>
            </button>

            {isExpanded && (
                <div className="border-t border-gray-200">
                    <div className="p-4 space-y-4">
                        {/* Category Summary */}
                        <div className="grid grid-cols-2 gap-4 p-3 bg-white bg-opacity-50 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-600">Total Weighted Score:</span>
                                <p className={`text-lg font-bold ${categoryColor.text}`}>
                                    {computation.total_weighted_score.toFixed(2)}
                                </p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-600">Total Weight:</span>
                                <p className={`text-lg font-bold ${categoryColor.text}`}>
                                    {formatPercentage(computation.total_weight)}
                                </p>
                            </div>
                        </div>

                        {/* Ratio Details */}
                        <div className="space-y-3">
                            <h5 className={`font-medium ${categoryColor.text} text-sm`}>
                                Individual Ratio Computations:
                            </h5>
                            {computation.ratios.map((ratio, index) => (
                                <RatioComputationCard
                                    key={index}
                                    ratio={ratio}
                                    categoryColor={categoryColor}
                                />
                            ))}
                        </div>

                        {/* Warnings */}
                        {computation.warnings.length > 0 && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <h6 className="text-sm font-medium text-yellow-900">Warnings:</h6>
                                        <ul className="text-sm text-yellow-800 mt-1 space-y-1">
                                            {computation.warnings.map((warning, index) => (
                                                <li key={index}>• {warning}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

interface RatioComputationCardProps {
    ratio: RatioComputation;
    categoryColor: {
        bg: string;
        text: string;
        icon: string;
    };
}

const RatioComputationCard: React.FC<RatioComputationCardProps> = ({ ratio, categoryColor }) => {

    return (
        <div className="bg-white bg-opacity-60 rounded-lg p-3 border border-gray-200">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <h6 className={`font-medium ${categoryColor.text} capitalize`}>
                        {ratio.ratio_name.replace(/_/g, ' ')}
                    </h6>
                    {ratio.is_concerning && (
                        <div title="This ratio value is concerning">
                            <AlertCircle className="w-4 h-4 text-orange-500" />
                        </div>
                    )}
                    {!ratio.is_concerning && ratio.source_value !== null && (
                        <div title="This ratio value is healthy">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                    )}
                </div>
                <div className="text-right">
                    <div className={`text-lg font-bold ${categoryColor.text}`}>
                        {ratio.normalized_score.toFixed(1)}/100
                    </div>
                    <div className="text-xs text-gray-600">
                        Weight: {formatPercentage(ratio.weight_in_category)}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                    <span className="font-medium text-gray-600">Source Value:</span>
                    <p className={`${categoryColor.text} font-medium`}>
                        {formatValue(ratio.source_value)}
                    </p>
                </div>
                <div>
                    <span className="font-medium text-gray-600">Benchmark:</span>
                    <p className={`${categoryColor.text} font-medium`}>
                        {formatValue(ratio.benchmark_value)}
                    </p>
                </div>
                <div>
                    <span className="font-medium text-gray-600">Weighted Score:</span>
                    <p className={`${categoryColor.text} font-medium`}>
                        {ratio.weighted_score.toFixed(2)}
                    </p>
                </div>
            </div>

            {/* Calculation Formula */}
            {ratio.calculation_formula && (
                <div className="mt-3 p-2 bg-gray-50 rounded border">
                    <span className="text-xs font-medium text-gray-700">Formula: </span>
                    <span className="text-xs text-gray-600">{ratio.calculation_formula}</span>
                </div>
            )}

            {/* Source Data Fields */}
            {ratio.source_data_fields && ratio.source_data_fields.length > 0 && (
                <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                    <div className="text-xs font-medium text-blue-900 mb-2">Source Financial Data:</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {ratio.source_data_fields.map((field, index) => (
                            <div key={index} className="text-xs">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-blue-800 capitalize">
                                        {field.field_name.replace(/_/g, ' ')}:
                                    </span>
                                    <span className="text-blue-700 font-semibold">
                                        {formatValue(field.field_value)}
                                    </span>
                                </div>
                                <div className="text-blue-600 opacity-75 text-xs">
                                    from {field.statement_type.replace(/_/g, ' ')}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-3 pt-2 border-t border-gray-200">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-gray-500" />
                    <span className="text-xs text-gray-600">
                        {getMethodDescription(ratio.normalization_method)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ComputationDetails;
